import type { CaseResponseDto } from '@bandido/shared/dtos';
import { MAX_SEARCH_PASSENGERS } from './searchPassengers';

export const BOOKING_CASE_PARAM = 'caseId';

export const TRAVEL_SERVICE_ROUTES = [
  '/hotels',
  '/transfers',
  '/experiences',
  '/esims',
  '/trenes',
  '/buses',
  '/pases',
  '/circuitos',
  '/ferries',
  '/cars',
] as const;

export const ROSTER_PARTY_ROUTES = new Set([
  '/transfers',
  '/experiences',
  '/trenes',
  '/buses',
  '/ferries',
]);

export interface BookingPartySuggestion {
  size: number;
  revision: number;
}

export function currentCaseRoster<T>(
  data: T | undefined,
  isPreviousData: boolean,
): T | null {
  return data && !isPreviousData ? data : null;
}

export function nextBookingPartySuggestion(
  current: BookingPartySuggestion | null,
  size: number,
): BookingPartySuggestion {
  return { size, revision: (current?.revision ?? 0) + 1 };
}

export function consumeBookingPartySuggestion(
  current: BookingPartySuggestion | null,
  revision: number,
): BookingPartySuggestion | null {
  return current?.revision === revision ? null : current;
}

export function bookingCaseChangeLocked(
  hasAttempt: boolean,
  holdLoading: boolean,
  retryLoading: boolean,
  profileSaving = false,
): boolean {
  return hasAttempt || holdLoading || retryLoading || profileSaving;
}

export function rosterPartySuggestionAvailable(count: number): boolean {
  return count > 0 && count <= MAX_SEARCH_PASSENGERS;
}

export function bookingCaseId(raw: string | null): number | null {
  if (!raw || !/^\d+$/.test(raw)) return null;
  const id = Number(raw);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

export function operatingAgencyId(value: string | number): number {
  const agencyId = Number(value);
  return Number.isSafeInteger(agencyId) && agencyId > 0 ? agencyId : 0;
}

export function bookingCaseEligible(
  candidate: CaseResponseDto | null | undefined,
  agencyId: string | number,
): candidate is CaseResponseDto {
  return Boolean(
    candidate &&
      candidate.agencyId === operatingAgencyId(agencyId) &&
      candidate.clientId,
  );
}

export function caseCanStartTravelSearch(
  caseData: Pick<CaseResponseDto, 'agencyId' | 'clientId'>,
  agencyId: string | number,
  canViewTravelServices: boolean,
): boolean {
  return Boolean(
    canViewTravelServices &&
      caseData.clientId &&
      caseData.agencyId === operatingAgencyId(agencyId),
  );
}

export function bookingCaseCreationReady(subject: string, clientId?: string) {
  return Boolean(subject.trim() && clientId);
}

export function withBookingCase(route: string, caseId: number): string {
  const separator = route.includes('?') ? '&' : '?';
  return `${route}${separator}${BOOKING_CASE_PARAM}=${caseId}`;
}

export function mergeBookingCaseContext(search: string, caseId: string | null): string {
  const params = new URLSearchParams(search);
  if (caseId) params.set(BOOKING_CASE_PARAM, caseId);
  else params.delete(BOOKING_CASE_PARAM);
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}
